import { backendApiClient } from './BackendApiClient';
import type {
  UserProfile, Organization, Member, Post, Meeting, PaymentPeriod,
  Folder, Document, Proposal, Notifications, Attachment,
  FinancialYearSummary, FinancialYearDetail, SupplierInvoice, OtherCost,
  NotifyMemberInvoiceResult, NewMember, MemberImportPreview, ImportMembersResult,
} from '../types/models';

// --- Profile ---
export const getProfile = (): Promise<UserProfile> =>
  backendApiClient.get<UserProfile>('/users/me');

export const updateProfile = (data: { fullName: string; phone: string }): Promise<UserProfile> =>
  backendApiClient.put<UserProfile>('/users/me', data);

// --- Organizations ---
export const getOrganizations = (): Promise<Organization[]> =>
  backendApiClient.get<Organization[]>('/organizations');

export const createOrganization = (data: { name: string; description: string; orgNumber: string | null }): Promise<Organization> =>
  backendApiClient.post<Organization>('/organizations', data);

export const setOrganizationVerified = (orgId: string, isVerified: boolean): Promise<void> =>
  backendApiClient.put<void>(`/organizations/${orgId}/verified`, { isVerified });

export const deleteOrganization = (orgId: string): Promise<void> =>
  backendApiClient.delete(`/organizations/${orgId}`);

export const updateOrganization = (orgId: string, data: { name: string; description: string; orgNumber: string }): Promise<Organization> =>
  backendApiClient.put<Organization>(`/organizations/${orgId}`, data);

export const updateOrganizationBilling = (orgId: string, data: {
  billingBank: string | null; billingAccount: string | null; billingPaymentDays: number | null;
  paymentRemindersEnabled: boolean; paymentReminderDays: number;
}): Promise<Organization> =>
  backendApiClient.put<Organization>(`/organizations/${orgId}/billing-info`, data);

// --- Members ---
export const getMembers = (orgId: string): Promise<Member[]> =>
  backendApiClient.get<Member[]>(`/organizations/${orgId}/members`);

export const createMember = (orgId: string, data: NewMember): Promise<Member> =>
  backendApiClient.post<Member>(`/organizations/${orgId}/members`, data);

export const previewMemberImport = (orgId: string, file: File): Promise<MemberImportPreview> => {
  const fd = new FormData();
  fd.append('file', file);
  return backendApiClient.postFormData<MemberImportPreview>(`/organizations/${orgId}/members/import/preview`, fd);
};

// Every imported member gets an invite email sent inside the request, so large imports take minutes.
const IMPORT_TIMEOUT_MS = 300_000;

export const importMembers = (orgId: string, members: NewMember[]): Promise<ImportMembersResult> =>
  backendApiClient.post<ImportMembersResult>(`/organizations/${orgId}/members/import`, { members }, IMPORT_TIMEOUT_MS);

export const updateMember = (orgId: string, memberId: string, data: {
  fullName: string; phone: string; propertyNumber: string; sharePercentage: number; role: string;
}): Promise<Member> =>
  backendApiClient.put<Member>(`/organizations/${orgId}/members/${memberId}`, data);

export const updateMemberEmail = (orgId: string, memberId: string, email: string): Promise<Member> =>
  backendApiClient.patch<Member>(`/organizations/${orgId}/members/${memberId}/email`, { email });

export const exportMembers = (orgId: string): Promise<void> =>
  backendApiClient.downloadFile(`/organizations/${orgId}/members/export`, 'medlemmar.csv');

export const removeMember = (orgId: string, memberId: string): Promise<void> =>
  backendApiClient.delete(`/organizations/${orgId}/members/${memberId}`);

// --- Posts ---
export const getPosts = (orgId: string): Promise<Post[]> =>
  backendApiClient.get<Post[]>(`/organizations/${orgId}/posts`);

export const createPost = (orgId: string, data: { title: string; content: string }): Promise<Post> =>
  backendApiClient.post<Post>(`/organizations/${orgId}/posts`, data);

export const updatePost = (orgId: string, postId: string, data: { title: string; content: string }): Promise<Post> =>
  backendApiClient.put<Post>(`/organizations/${orgId}/posts/${postId}`, data);

export const deletePost = (orgId: string, postId: string): Promise<void> =>
  backendApiClient.delete(`/organizations/${orgId}/posts/${postId}`);

export const uploadPostImage = (orgId: string, postId: string, file: File): Promise<{ imageUrl: string }> => {
  const fd = new FormData();
  fd.append('file', file);
  return backendApiClient.postFormData<{ imageUrl: string }>(`/organizations/${orgId}/posts/${postId}/image`, fd);
};

export const uploadPostAttachment = (orgId: string, postId: string, file: File): Promise<Attachment> => {
  const fd = new FormData();
  fd.append('file', file);
  return backendApiClient.postFormData<Attachment>(`/organizations/${orgId}/posts/${postId}/attachments`, fd);
};

export const deletePostAttachment = (orgId: string, postId: string, attachmentId: string): Promise<void> =>
  backendApiClient.delete(`/organizations/${orgId}/posts/${postId}/attachments/${attachmentId}`);

export const addComment = (orgId: string, postId: string, content: string): Promise<import('../types/models').Comment> =>
  backendApiClient.post(`/organizations/${orgId}/posts/${postId}/comments`, { content });

export const deleteComment = (orgId: string, postId: string, commentId: string): Promise<void> =>
  backendApiClient.delete(`/organizations/${orgId}/posts/${postId}/comments/${commentId}`);

// --- Meetings ---
export const getMeetings = (orgId: string): Promise<Meeting[]> =>
  backendApiClient.get<Meeting[]>(`/organizations/${orgId}/meetings`);

export const createMeeting = (orgId: string, data: {
  title: string; description: string; location: string; meetingDate: string; notificationChannel: 'sms' | 'email';
}): Promise<Meeting> =>
  backendApiClient.post<Meeting>(`/organizations/${orgId}/meetings`, data);

export const updateMeeting = (orgId: string, meetingId: string, data: {
  title: string; description: string; location: string; meetingDate: string;
}): Promise<Meeting> =>
  backendApiClient.put<Meeting>(`/organizations/${orgId}/meetings/${meetingId}`, data);

export const deleteMeeting = (orgId: string, meetingId: string): Promise<void> =>
  backendApiClient.delete(`/organizations/${orgId}/meetings/${meetingId}`);

export const setRsvp = (orgId: string, meetingId: string, status: string): Promise<void> =>
  backendApiClient.post(`/organizations/${orgId}/meetings/${meetingId}/rsvp`, { status });

export const uploadMeetingAttachment = (orgId: string, meetingId: string, file: File): Promise<Attachment> => {
  const fd = new FormData();
  fd.append('file', file);
  return backendApiClient.postFormData<Attachment>(`/organizations/${orgId}/meetings/${meetingId}/attachments`, fd);
};

export const deleteMeetingAttachment = (orgId: string, meetingId: string, attachmentId: string): Promise<void> =>
  backendApiClient.delete(`/organizations/${orgId}/meetings/${meetingId}/attachments/${attachmentId}`);

export const saveProtocol = (orgId: string, meetingId: string, data: { title: string; content: string }): Promise<import('../types/models').Protocol> =>
  backendApiClient.post(`/organizations/${orgId}/meetings/${meetingId}/protocol`, data);

export const approveProtocol = (orgId: string, meetingId: string, data: { status: string; comment?: string }): Promise<void> =>
  backendApiClient.post(`/organizations/${orgId}/meetings/${meetingId}/protocol/approve`, data);

export const addProtocolApprover = (orgId: string, meetingId: string, userId: string): Promise<import('../types/models').ProtocolApproval> =>
  backendApiClient.post(`/organizations/${orgId}/meetings/${meetingId}/protocol/approvers`, { userId });

// --- Payments ---
export const getPaymentPeriods = (orgId: string): Promise<PaymentPeriod[]> =>
  backendApiClient.get<PaymentPeriod[]>(`/organizations/${orgId}/payments`);

export const createPaymentPeriod = (orgId: string, data: {
  title: string; description: string; amount: number; dueDate: string; financialYearId?: string | null;
}): Promise<PaymentPeriod> =>
  backendApiClient.post<PaymentPeriod>(`/organizations/${orgId}/payments`, data);

export const updatePaymentPeriod = (orgId: string, periodId: string, data: {
  title: string; description: string; amount: number; dueDate: string; financialYearId?: string | null;
}): Promise<PaymentPeriod> =>
  backendApiClient.put<PaymentPeriod>(`/organizations/${orgId}/payments/${periodId}`, data);

export const deletePaymentPeriod = (orgId: string, periodId: string): Promise<void> =>
  backendApiClient.delete(`/organizations/${orgId}/payments/${periodId}`);

export const updateMemberPayment = (orgId: string, periodId: string, paymentId: string, isPaid: boolean): Promise<void> =>
  backendApiClient.put(`/organizations/${orgId}/payments/${periodId}/members/${paymentId}`, { isPaid });

export const regenerateInvoices = (orgId: string, periodId: string): Promise<PaymentPeriod> =>
  backendApiClient.post<PaymentPeriod>(`/organizations/${orgId}/payments/${periodId}/regenerate-invoices`);

export const exportPayments = (orgId: string): Promise<void> =>
  backendApiClient.downloadFile(`/organizations/${orgId}/payments/export`, 'betalningar.csv');

export const downloadInvoice = (orgId: string, periodId: string, paymentId: string, fileName: string): Promise<void> =>
  backendApiClient.downloadFile(`/organizations/${orgId}/payments/${periodId}/members/${paymentId}/invoice`, fileName);

export const notifyMemberInvoice = (orgId: string, periodId: string, paymentId: string): Promise<NotifyMemberInvoiceResult> =>
  backendApiClient.post<NotifyMemberInvoiceResult>(`/organizations/${orgId}/payments/${periodId}/members/${paymentId}/notify`);

// --- Financial years (Ekonomi) ---
export const getFinancialYears = (orgId: string): Promise<FinancialYearSummary[]> =>
  backendApiClient.get<FinancialYearSummary[]>(`/organizations/${orgId}/financial-years`);

export const getFinancialYear = (orgId: string, yearId: string): Promise<FinancialYearDetail> =>
  backendApiClient.get<FinancialYearDetail>(`/organizations/${orgId}/financial-years/${yearId}`);

export const createFinancialYear = (orgId: string, data: {
  year: number; startDate: string; endDate: string; openingBalance: number;
}): Promise<FinancialYearSummary> =>
  backendApiClient.post<FinancialYearSummary>(`/organizations/${orgId}/financial-years`, data);

export const updateFinancialYear = (orgId: string, yearId: string, data: {
  startDate: string; endDate: string; openingBalance: number;
}): Promise<FinancialYearSummary> =>
  backendApiClient.put<FinancialYearSummary>(`/organizations/${orgId}/financial-years/${yearId}`, data);

export const closeFinancialYear = (orgId: string, yearId: string): Promise<FinancialYearSummary> =>
  backendApiClient.post<FinancialYearSummary>(`/organizations/${orgId}/financial-years/${yearId}/close`);

export const reopenFinancialYear = (orgId: string, yearId: string): Promise<FinancialYearSummary> =>
  backendApiClient.post<FinancialYearSummary>(`/organizations/${orgId}/financial-years/${yearId}/reopen`);

export const generateFinancialYearReport = (orgId: string, yearId: string): Promise<FinancialYearSummary> =>
  backendApiClient.post<FinancialYearSummary>(`/organizations/${orgId}/financial-years/${yearId}/generate-report`);

export const deleteFinancialYear = (orgId: string, yearId: string): Promise<void> =>
  backendApiClient.delete(`/organizations/${orgId}/financial-years/${yearId}`);

export const downloadYearReport = (orgId: string, yearId: string, fileName: string): Promise<void> =>
  backendApiClient.downloadFile(`/organizations/${orgId}/financial-years/${yearId}/report`, fileName);

export const createSupplierInvoice = (orgId: string, yearId: string, data: {
  supplierName: string; description: string; amount: number; invoiceDate: string; dueDate: string;
}): Promise<SupplierInvoice> =>
  backendApiClient.post<SupplierInvoice>(`/organizations/${orgId}/financial-years/${yearId}/supplier-invoices`, data);

export const uploadSupplierInvoiceFile = (orgId: string, yearId: string, invoiceId: string, file: File): Promise<SupplierInvoice> => {
  const fd = new FormData();
  fd.append('file', file);
  return backendApiClient.postFormData<SupplierInvoice>(
    `/organizations/${orgId}/financial-years/${yearId}/supplier-invoices/${invoiceId}/file`, fd);
};

export const updateSupplierInvoice = (orgId: string, yearId: string, invoiceId: string, data: {
  supplierName: string; description: string; amount: number; invoiceDate: string; dueDate: string; isPaid: boolean;
}): Promise<SupplierInvoice> =>
  backendApiClient.put<SupplierInvoice>(`/organizations/${orgId}/financial-years/${yearId}/supplier-invoices/${invoiceId}`, data);

export const deleteSupplierInvoice = (orgId: string, yearId: string, invoiceId: string): Promise<void> =>
  backendApiClient.delete(`/organizations/${orgId}/financial-years/${yearId}/supplier-invoices/${invoiceId}`);

export const downloadSupplierInvoiceFile = (orgId: string, yearId: string, invoiceId: string, fileName: string): Promise<void> =>
  backendApiClient.downloadFile(`/organizations/${orgId}/financial-years/${yearId}/supplier-invoices/${invoiceId}/download`, fileName);

export const createOtherCost = (orgId: string, yearId: string, data: {
  description: string; category: string; amount: number; costDate: string;
}): Promise<OtherCost> =>
  backendApiClient.post<OtherCost>(`/organizations/${orgId}/financial-years/${yearId}/other-costs`, data);

export const uploadOtherCostFile = (orgId: string, yearId: string, costId: string, file: File): Promise<OtherCost> => {
  const fd = new FormData();
  fd.append('file', file);
  return backendApiClient.postFormData<OtherCost>(
    `/organizations/${orgId}/financial-years/${yearId}/other-costs/${costId}/file`, fd);
};

export const updateOtherCost = (orgId: string, yearId: string, costId: string, data: {
  description: string; category: string; amount: number; costDate: string;
}): Promise<OtherCost> =>
  backendApiClient.put<OtherCost>(`/organizations/${orgId}/financial-years/${yearId}/other-costs/${costId}`, data);

export const deleteOtherCost = (orgId: string, yearId: string, costId: string): Promise<void> =>
  backendApiClient.delete(`/organizations/${orgId}/financial-years/${yearId}/other-costs/${costId}`);

export const downloadOtherCostFile = (orgId: string, yearId: string, costId: string, fileName: string): Promise<void> =>
  backendApiClient.downloadFile(`/organizations/${orgId}/financial-years/${yearId}/other-costs/${costId}/download`, fileName);

// --- Documents ---
export const getFolders = (orgId: string): Promise<Folder[]> =>
  backendApiClient.get<Folder[]>(`/organizations/${orgId}/folders`);

export const createFolder = (orgId: string, data: { name: string; parentFolderId?: string | null }): Promise<Folder> =>
  backendApiClient.post<Folder>(`/organizations/${orgId}/folders`, data);

export const deleteFolder = (orgId: string, folderId: string): Promise<void> =>
  backendApiClient.delete(`/organizations/${orgId}/folders/${folderId}`);

export const getDocuments = (orgId: string, folderId: string): Promise<Document[]> =>
  backendApiClient.get<Document[]>(`/organizations/${orgId}/folders/${folderId}/documents`);

export const uploadDocument = (orgId: string, folderId: string, file: File): Promise<Document> => {
  const fd = new FormData();
  fd.append('file', file);
  return backendApiClient.postFormData<Document>(`/organizations/${orgId}/folders/${folderId}/documents`, fd);
};

export const deleteDocument = (orgId: string, documentId: string): Promise<void> =>
  backendApiClient.delete(`/organizations/${orgId}/documents/${documentId}`);

export const downloadDocument = (orgId: string, documentId: string, fileName: string): Promise<void> =>
  backendApiClient.downloadFile(`/organizations/${orgId}/documents/${documentId}/download`, fileName);

// --- Proposals ---
export const getProposals = (orgId: string): Promise<Proposal[]> =>
  backendApiClient.get<Proposal[]>(`/organizations/${orgId}/proposals`);

export const createProposal = (orgId: string, data: {
  title: string; content: string; linkedMeetingId?: string | null;
}): Promise<Proposal> =>
  backendApiClient.post<Proposal>(`/organizations/${orgId}/proposals`, data);

export const updateProposalStatus = (orgId: string, proposalId: string, status: string): Promise<void> =>
  backendApiClient.put(`/organizations/${orgId}/proposals/${proposalId}/status`, { status });

export const deleteProposal = (orgId: string, proposalId: string): Promise<void> =>
  backendApiClient.delete(`/organizations/${orgId}/proposals/${proposalId}`);

// --- Notifications ---
export const getNotifications = (orgId: string): Promise<Notifications> =>
  backendApiClient.get<Notifications>(`/organizations/${orgId}/notifications`);

export const markSectionSeen = (orgId: string, section: string): Promise<void> =>
  backendApiClient.put(`/organizations/${orgId}/notifications/${section}`, {});

// --- Subscription ---
export interface SubscriptionStatus {
  status: string;
  trialEndsAt: string | null;
  paidUntil: string | null;
  publishableKey: string;
  tier: string;
  cancelAtPeriodEnd: boolean;
  checkoutEnabled: boolean;
}

export const getSubscriptionStatus = (orgId: string): Promise<SubscriptionStatus> =>
  backendApiClient.get<SubscriptionStatus>(`/organizations/${orgId}/subscription`);

export const createCheckoutSession = (orgId: string, successUrl: string, cancelUrl: string): Promise<{ checkoutUrl: string }> =>
  backendApiClient.post<{ checkoutUrl: string }>(`/organizations/${orgId}/subscription/checkout`, { successUrl, cancelUrl });

export const createPortalSession = (orgId: string, returnUrl: string): Promise<{ portalUrl: string }> =>
  backendApiClient.post<{ portalUrl: string }>(`/organizations/${orgId}/subscription/portal`, { returnUrl });
